import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import ProductCard from "./ProductCard";
import { supabase } from "@/supabase";
import product1 from "@/assets/product-1.jpg";
import product2 from "@/assets/product-2.jpg";
import product3 from "@/assets/product-3.jpg";

// Mapa de imagens para fallback
const imageMap: Record<string, string> = {
  "/assets/product-1.jpg": product1,
  "/assets/product-2.jpg": product2,
  "/assets/product-3.jpg": product3,
};

const ProductCatalog = () => {
  const { data: products, isLoading, error } = useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Erro ao buscar produtos:", error);
        throw error;
      }

      // Mapear dados para o formato esperado pelo ProductCard
      return data?.map((product) => ({
        id: product.id,
        name: product.name,
        price: product.price,
        image: imageMap[product.image_url] || product.image_url,
        colors: product.colors || [],
        sizes: product.sizes || [],
        stockQuantity: product.stock_quantity,
        stockStatus: product.stock_status,
        isOnDemand: product.is_on_demand || false,
        description: product.description,
      })) || [];
    },
  });

  // Loading state
  if (isLoading) {
    return (
      <section id="produtos" className="section-padding">
        <div className="container-narrow">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground mb-4">
              Nossa Coleção
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Carregando produtos...
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="bg-card rounded-lg p-6 animate-pulse"
              >
                <div className="bg-muted h-48 rounded-lg mb-4" />
                <div className="bg-muted h-4 rounded mb-2" />
                <div className="bg-muted h-4 rounded w-3/4" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section id="produtos" className="section-padding">
        <div className="container-narrow">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground mb-4">
              Nossa Coleção
            </h2>
            <p className="text-destructive max-w-2xl mx-auto">
              Erro ao carregar produtos. Tente novamente mais tarde.
            </p>
          </div>
        </div>
      </section>
    );
  }

  // Empty state
  if (!products || products.length === 0) {
    return (
      <section id="produtos" className="section-padding">
        <div className="container-narrow">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground mb-4">
              Nossa Coleção
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Nenhum produto disponível no momento.
            </p>
          </div>
        </div>
      </section>
    );
  }

  // Success state with products
  return (
    <section id="produtos" className="section-padding">
      <div className="container-narrow">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground mb-4">
            Nossa Coleção
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Peças únicas feitas à mão com amor e dedicação. Cada criação carrega a
            essência do trabalho artesanal.
          </p>
          <div className="decoration-line max-w-xs mx-auto mt-6" />
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {products.map((product) => (
            <ProductCard
              key={product.id}
              {...product}
            />
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-sm text-muted-foreground mt-10"
        >
          Por serem artesanais, cada peça é única e pode ter pequenas variações naturais.
        </motion.p>
      </div>
    </section>
  );
};

export default ProductCatalog;